import { LoadingButton } from '@mui/lab';
import { Box, Grid, styled, Typography } from '@mui/material';
import { fallbackContractCountry, getCurrencySymbolByCountryCode } from 'components/common/Select/CountrySelect';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buyDayPass } from 'services/subscriptionService';
import { getDayPassPriceFromCurrency } from 'utils/currency';
import { showError } from 'utils/errorHandler';

const StyledBuyNowButton = styled(LoadingButton)(({ theme }) => ({
  width: '120px',
  borderRadius: '8px',
  '&.MuiLoadingButton-loading': {
    backgroundColor: theme.palette.primary.main,
    opacity: 1,
  },
}));

const StyledActiveButton = styled(StyledBuyNowButton)(({ theme }) => ({
  backgroundColor: '#23A138',
  color: theme.palette.grey[100],
}));

export default function DayPass({ isSubscriptionPage }: { isSubscriptionPage?: boolean }) {
  const authContext = useContext(AuthContext);
  const dayPassActive =
    authContext?.user?.dayPassActiveUntil && isUtcDateBeforeNow(authContext.user.dayPassActiveUntil);
  const shouldRender = !authContext.user?.subscriptionActive || dayPassActive;
  const currency = authContext.viewerCountry;

  if (!shouldRender) return <></>;

  const viewerCurrencySymbol = getCurrencySymbolByCountryCode(currency ?? fallbackContractCountry.code);
  const price = getDayPassPriceFromCurrency(viewerCurrencySymbol ?? fallbackContractCountry.currencySymbol);

  return (
    <Box
      sx={{
        position: 'relative',
        maxWidth: theme => theme.breakpoints.values.lg,
        width: '100%',
        marginBottom: { xs: isSubscriptionPage ? '20px' : 0, md: 0 },
      }}
    >
      <Box
        sx={{
          position: { sm: 'relative', md: isSubscriptionPage ? 'absolute' : 'relative' },
          top: { sm: 0, md: isSubscriptionPage ? -20 : 0 },
          right: '0%',
          width: { xs: '100%', md: isSubscriptionPage ? '264px' : '100%' },
          height: { xs: '91px', md: isSubscriptionPage ? '91px' : '80px' },
          borderRadius: { xs: '12px', md: isSubscriptionPage ? '12px 12px 0px 12px' : '12px' },
          backgroundColor: '#1C253C',
          zIndex: 1,
          padding: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '36px',
            justifyContent: 'space-between',
          }}
        >
          <Grid container alignItems="center" order={1}>
            <Grid item xs={2} sm={1} md={isSubscriptionPage ? 3 : 0.8}>
              <Typography variant="h4" fontWeight="bold" color="white">
                {viewerCurrencySymbol}
                {price}
              </Typography>
            </Grid>
            <Grid item xs={2} sm={1} md={isSubscriptionPage ? 3 : 0.8} order={2}>
              <Typography variant="subtitle2" color="white" fontSize="14px">
                DAY PASS
              </Typography>
            </Grid>
            <Grid
              container
              item
              xs={8}
              sm={10}
              md={5}
              justifyContent="right"
              order={{
                xs: 3,
                md: isSubscriptionPage ? 3 : 4,
              }}
            >
              {!dayPassActive ? (
                <BuyNowBtn />
              ) : (
                <StyledActiveButton variant="contained" color="success">
                  Active
                </StyledActiveButton>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              md={isSubscriptionPage ? 12 : 5}
              order={{
                xs: 4,
                md: isSubscriptionPage ? 4 : 3,
              }}
            >
              {!dayPassActive ? (
                <Typography
                  variant="body2"
                  color="white"
                  fontSize="12px"
                  mt={{ sx: 0, md: isSubscriptionPage ? undefined : '16px' }}
                >
                  24h premium access begins immediately
                </Typography>
              ) : (
                <>
                  {isSubscriptionPage && (
                    <Typography
                      variant="body2"
                      color="white"
                      fontSize="12px"
                      mt={{ sx: 0, md: isSubscriptionPage ? undefined : '16px' }}
                    >
                      24h premium access currently active
                    </Typography>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}

function BuyNowBtn() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleBuyNow = () => {
    setLoading(true);
    if (!authContext.user) {
      navigate('/login');
      return;
    }
    buyDayPass(`${window.location.pathname}`)
      .then(url => (window.location.href = url))
      .catch(showError)
      .finally(() => setLoading(false));
  };

  return (
    <StyledBuyNowButton variant="contained" loading={loading} onClick={handleBuyNow}>
      Buy Now
    </StyledBuyNowButton>
  );
}

export function isUtcDateBeforeNow(utcDateString: string) {
  const nowUtc = new Date();
  const nowUtcTimestamp = nowUtc.getTime();

  const serverDate = new Date(utcDateString);
  const serverUtcTimestamp = serverDate.getTime();

  return nowUtcTimestamp < serverUtcTimestamp;
}
